import React, { useEffect, useRef, useState } from "react";

import DownloadLink from "../../components/DownloadLink";
import { BoutonHorsConnexion, PdfLocal, useFichierHorsConnexion } from "../../components/HorsConnexion";
import { useEnLigne } from "../../services/horsConnexion";
import { examDocumentFilename } from "../services/examDownloadFilename";
import { listTeacherExams } from "../services/teacherApi";

const modeConfig = {
  exams: { title: "Mes épreuves", eyebrow: "Toutes vos épreuves", view: undefined },
  drafts: { title: "Brouillons", eyebrow: "Travail à poursuivre", view: "drafts" },
  history: { title: "Historique", eyebrow: "Épreuves générées", view: "history" },
};

const statusLabels = {
  BROUILLON: "Brouillon",
  EN_ATTENTE_PAIEMENT: "En attente de paiement",
  PAYEE: "Payée",
  ERREUR: "Erreur",
};

const generationLabels = {
  PENDING: "En attente",
  PROCESSING: "Génération en cours",
  SUCCESS: "Générée",
  FAILED: "Échec de génération",
};

const documentKinds = [
  ["has_pdf", "pdf", "PDF", "sujet.pdf", "application/pdf"],
  ["has_docx", "docx", "DOCX", "sujet.docx", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  ["has_correction_pdf", "correction", "Correction", "corrige.pdf", "application/pdf"],
];

const readableDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
};

// Document d'une épreuve payée : téléchargement en ligne, et pour les PDF
// (sujet, correction) copie gardée sur l'appareil lisible sans réseau.
function DocumentEpreuve({ exam, field, endpoint, label, mimeType }) {
  const enLigne = useEnLigne();
  const cle = `epreuve:${exam.id}:${endpoint}`;
  const local = useFichierHorsConnexion(mimeType === "application/pdf" ? cle : null);
  const [ouvert, setOuvert] = useState(false);
  const url = `/api/teacher/exams/${exam.id}/files/${endpoint}/`;
  const titre = `${exam.intitule_session || `Épreuve #${exam.id}`} — ${label === "PDF" ? "Sujet" : label}`;
  return (
    <span className="ql-exam-document">
      {local ? (
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setOuvert(true)}>
          <i className="bi bi-cloud-check-fill me-1" />{label}
        </button>
      ) : enLigne ? (
        <DownloadLink url={url} filename={examDocumentFilename(exam, field)} mimeType={mimeType} authenticated className="btn btn-outline-primary btn-sm">{label}</DownloadLink>
      ) : <span className="unavailable">{label}</span>}
      {mimeType === "application/pdf" && (
        <BoutonHorsConnexion compact cle={cle} url={url} titre={titre} rubrique="epreuves"
          details={{ module: [exam.matiere, exam.classe].filter(Boolean).join(" · ") }} />
      )}
      {ouvert && local && <PdfLocal plein adresse={local.adresse} titre={titre} onClose={() => setOuvert(false)} />}
    </span>
  );
}

export default function TeacherExamList({ mode = "exams", onBack, onOpenExam }) {
  const config = modeConfig[mode] || modeConfig.exams;
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [openingId, setOpeningId] = useState(null);
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const openingRef = useRef(false);

  useEffect(() => {
    let active = true;
    setState((current) => ({ ...current, loading: true, error: null }));
    listTeacherExams({ view: config.view, page, pageSize: 10 }).then((data) => {
      if (active) setState({ loading: false, error: null, data });
    }).catch((error) => {
      if (active) setState({
        loading: false,
        error: error.response?.data?.detail || "Impossible de charger vos épreuves.",
        data: null,
      });
    });
    return () => { active = false; };
  }, [config.view, page, reloadKey]);

  const openExam = (examId) => {
    if (openingRef.current) return;
    openingRef.current = true;
    setOpeningId(examId);
    onOpenExam(examId);
  };

  const exams = state.data?.results || [];
  const totalPages = state.data?.totalPages || 0;

  return (
    <main className="ql-exam-list-main">
      <div className="ql-exam-toolbar">
        <button type="button" className="ql-exam-back" onClick={onBack}><i className="bi bi-arrow-left" /> Dashboard</button>
        <div><span className="ql-teacher-eyebrow">{config.eyebrow}</span><h1>{config.title}</h1></div>
        <span className="ql-exam-list-count">{state.data?.count || 0} épreuve(s)</span>
      </div>

      {state.loading && <section className="ql-teacher-state ql-exam-list-state" role="status"><span className="spinner-border text-primary" /><p>Chargement des épreuves…</p></section>}
      {!state.loading && state.error && <section className="ql-teacher-state ql-exam-list-state" role="alert"><i className="bi bi-wifi-off" /><p>{state.error}</p><button type="button" className="btn btn-primary" onClick={() => setReloadKey((current) => current + 1)}>Réessayer</button></section>}
      {!state.loading && !state.error && exams.length === 0 && <section className="ql-exam-list-empty"><i className="bi bi-folder2-open" /><h2>Aucune épreuve</h2><p>Aucune épreuve ne correspond à cette vue.</p></section>}

      {!state.loading && !state.error && exams.length > 0 && (
        <section className="ql-exam-list-grid" aria-label={config.title}>
          {exams.map((exam) => {
            const paid = exam.est_payee || exam.statut === "PAYEE";
            const draft = exam.statut === "BROUILLON";
            return (
              <article className="ql-exam-list-card" key={exam.id}>
                <div className="ql-exam-list-card-heading">
                  <div><span>#{exam.id}</span><h2>{exam.intitule_session || "Épreuve sans intitulé"}</h2></div>
                  <span className={`ql-exam-status ${String(exam.statut || "").toLowerCase()}`}>{statusLabels[exam.statut] || exam.statut || "—"}</span>
                </div>
                <dl>
                  <div><dt>Matière</dt><dd>{exam.matiere || "—"}</dd></div>
                  <div><dt>Classe</dt><dd>{exam.classe || "—"}</dd></div>
                  <div><dt>Session</dt><dd>{exam.session || "—"}</dd></div>
                  <div><dt>Palier</dt><dd>{exam.palier || "—"}</dd></div>
                  <div><dt>Génération</dt><dd>{generationLabels[exam.generation_status] || exam.generation_status || "—"}</dd></div>
                  <div><dt>Paiement</dt><dd>{paid ? "Payé" : "Non payé"}</dd></div>
                  <div><dt>Montant</dt><dd>{exam.montant ?? "—"} FCFA</dd></div>
                  <div><dt>Date</dt><dd>{readableDate(exam.date_generation)}</dd></div>
                </dl>
                <div className="ql-exam-list-documents" aria-label="Documents disponibles">
                  {documentKinds.map(([field, endpoint, label, filename, mimeType]) => paid && exam[field] ? (
                    <DocumentEpreuve key={field} exam={exam} field={field} endpoint={endpoint} label={label} mimeType={mimeType} />
                  ) : <span key={field} className={exam[field] ? "available" : "unavailable"}>{label}</span>)}
                </div>
                <div className="ql-exam-list-actions">
                  <button type="button" className="btn btn-primary" onClick={() => openExam(exam.id)} disabled={openingId !== null}>
                    {openingId === exam.id ? "Ouverture…" : draft ? "Reprendre" : paid ? "Voir le résultat" : "Voir le résultat et payer"}
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      )}

      {!state.loading && !state.error && totalPages > 1 && (
        <nav className="ql-exam-pagination" aria-label="Pagination des épreuves">
          <button type="button" className="btn btn-outline-primary" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Précédent</button>
          <span>Page {state.data.page} / {totalPages}</span>
          <button type="button" className="btn btn-outline-primary" disabled={!state.data.next} onClick={() => setPage((current) => current + 1)}>Suivant</button>
        </nav>
      )}
    </main>
  );
}
